import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { 
  login as loginAPI, 
  logout as logoutAPI, 
  refreshToken as refreshTokenAPI, 
  getCurrentUser
} from '../services/auth';

const AuthContext = createContext();

// Время жизни access токена (из бэкенда) — 60 минут
const ACCESS_TOKEN_LIFETIME_MS = 60 * 60 * 1000;
// Обновляем за 5 минут до истечения
const REFRESH_BEFORE_EXPIRY_MS = 5 * 60 * 1000;
// Интервал обновления = 55 минут
const REFRESH_INTERVAL_MS = ACCESS_TOKEN_LIFETIME_MS - REFRESH_BEFORE_EXPIRY_MS;
// Порог для принудительного refresh при возврате фокуса (50 минут)
const FORCE_REFRESH_THRESHOLD_MS = 50 * 60 * 1000;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const refreshTimerRef = useRef(null);
  const isRefreshingRef = useRef(false);
  const isLoggingOutRef = useRef(false); // Флаг для отслеживания процесса logout
  const isInitializingRef = useRef(true); // Флаг инициализации (не показываем модалку при первом 401)
  const userRef = useRef(user); // Ref для актуального значения user
  const lastRefreshTimeRef = useRef(Date.now()); // Время последнего успешного refresh
  
  // Синхронизируем ref с state
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Очистка таймера обновления
  const clearRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  // Обработка истечения сессии
  const handleSessionExpired = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    setUser(null);
    setSessionExpired(true);
    
    // Глобальное событие для других компонентов
    window.dispatchEvent(new CustomEvent('sessionExpired'));
  }, []);

  // Сброс флага истечения сессии
  const clearSessionExpired = useCallback(() => {
    setSessionExpired(false);
  }, []);

  // Функция обновления токена (используется рекурсивно)
  const doRefreshToken = useCallback(async () => {
    if (isRefreshingRef.current) return;
    
    // Не проверяем cookies — они httponly и недоступны JS
    // Просто пробуем refresh, бэкенд сам проверит cookies
    
    isRefreshingRef.current = true;
    console.log('[Auth] Token refresh triggered');
    
    try {
      await refreshTokenAPI();
      console.log('[Auth] Token refreshed successfully');
      lastRefreshTimeRef.current = Date.now(); // Запоминаем время успешного refresh
      
      // Планируем следующее обновление
      refreshTimerRef.current = setTimeout(doRefreshToken, REFRESH_INTERVAL_MS);
      console.log(`[Auth] Next refresh scheduled in ${REFRESH_INTERVAL_MS / 1000 / 60} minutes`);
    } catch (error) {
      console.error('[Auth] Token refresh failed:', error);
      // Показываем модалку только если пользователь был авторизован
      if (userRef.current) {
        handleSessionExpired();
      }
    } finally {
      isRefreshingRef.current = false;
    }
  }, [handleSessionExpired, clearRefreshTimer]);

  // Планирование обновления токена
  const scheduleTokenRefresh = useCallback(() => {
    // Очищаем предыдущий таймер
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
    
    // Планируем первое обновление
    refreshTimerRef.current = setTimeout(doRefreshToken, REFRESH_INTERVAL_MS);
    console.log(`[Auth] Token refresh scheduled in ${REFRESH_INTERVAL_MS / 1000 / 60} minutes`);
  }, [doRefreshToken]);

  // Инициализация при загрузке
  useEffect(() => {
    let isMounted = true;
    
    const initializeAuth = async () => {
      try {
        // Пробуем получить данные пользователя
        // Cookies httponly — JS не может их проверить, поэтому просто делаем запрос
        let userData = null;
        
        try {
          userData = await getCurrentUser();
          console.log('[Auth] User authenticated');
        } catch (error) {
          // Если 401 — access token истёк или отсутствует, пробуем refresh
          if (error.response?.status === 401) {
            console.log('[Auth] Access token expired or missing, trying refresh...');
            try {
              await refreshTokenAPI();
              console.log('[Auth] Refresh successful, getting user...');
              userData = await getCurrentUser();
            } catch (refreshError) {
              // Refresh не удался — пользователь не авторизован
              console.log('[Auth] Refresh failed, user not authenticated');
            }
          } else {
            // Другая ошибка (сеть, сервер)
            console.warn('[Auth] Failed to get user data:', error);
          }
        }

        if (isMounted) {
          setUser(userData);
          // Если пользователь авторизован, планируем обновление токена
          if (userData) {
            lastRefreshTimeRef.current = Date.now();
            scheduleTokenRefresh();
          }
        }
      } catch (error) {
        console.warn('[Auth] Initialization error:', error);
        if (isMounted) {
          setUser(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
          isInitializingRef.current = false;
        }
      }
    };

    initializeAuth();

    // Слушаем событие 401 от API interceptor
    const handleAuthError = () => {
      console.log('[Auth] Received auth error event');
      
      // Не показываем модалку если идёт процесс logout
      if (isLoggingOutRef.current) {
        console.log('[Auth] Ignoring auth error during logout');
        return;
      }
      
      // Не показываем модалку при инициализации (первый 401 — это норма)
      if (isInitializingRef.current) {
        console.log('[Auth] Ignoring auth error during initialization');
        return;
      }
      
      // Показываем модалку только если пользователь был авторизован
      if (userRef.current) {
        handleSessionExpired();
      }
    };
    
    window.addEventListener('authError', handleAuthError);
    
    return () => {
      isMounted = false;
      clearRefreshTimer();
      window.removeEventListener('authError', handleAuthError);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Запускается только при монтировании

  // Проверка при возврате фокуса на страницу (браузер может заморозить таймеры в фоне)
  useEffect(() => {
    const handleVisibilityChange = () => {
      // Проверяем только userRef — cookies httponly и недоступны JS
      if (document.visibilityState === 'visible' && userRef.current) {
        const timeSinceLastRefresh = Date.now() - lastRefreshTimeRef.current;
        console.log(`[Auth] Page visible, time since last refresh: ${Math.round(timeSinceLastRefresh / 1000 / 60)} minutes`);
        
        // Если прошло больше 50 минут - сразу делаем refresh
        if (timeSinceLastRefresh > FORCE_REFRESH_THRESHOLD_MS) {
          console.log('[Auth] Forcing token refresh after long inactivity');
          // Очищаем старый таймер и делаем refresh
          clearRefreshTimer();
          doRefreshToken();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [doRefreshToken, clearRefreshTimer]);

  // Логин
  const login = async (email, password) => {
    try {
      await loginAPI(email, password);
      const userData = await getCurrentUser();
      setUser(userData);
      setSessionExpired(false);
      lastRefreshTimeRef.current = Date.now(); // Запоминаем время логина
      
      // После успешного логина планируем обновление токена
      scheduleTokenRefresh();
      
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Ошибка авторизации'
      };
    }
  };

  // Логаут
  const logout = async () => {
    // Устанавливаем флаг logout, чтобы не показывать модалку при ошибке API
    isLoggingOutRef.current = true;
    
    clearRefreshTimer();
    
    // Сначала очищаем состояние, чтобы избежать показа модалки
    setUser(null);
    setSessionExpired(false);
    
    try {
      await logoutAPI();
    } catch (error) {
      // Игнорируем ошибки при logout — пользователь уже разлогинен локально
      console.warn('[Auth] Logout API call failed:', error);
    } finally {
      // Сбрасываем флаг после завершения
      isLoggingOutRef.current = false;
    }
  };

  // Ручное обновление токена (для внешнего использования)
  const refreshAuthToken = async () => {
    if (isRefreshingRef.current) {
      console.log('[Auth] Refresh already in progress, skipping');
      return;
    }
    
    isRefreshingRef.current = true;
    
    try {
      const response = await refreshTokenAPI();
      const userData = await getCurrentUser();
      setUser(userData);
      
      // Перепланируем обновление
      scheduleTokenRefresh();
      
      return response.access_token;
    } catch (error) {
      handleSessionExpired();
      throw error;
    } finally {
      isRefreshingRef.current = false;
    }
  };

  const value = {
    user,
    loading,
    login,
    logout,
    refreshAuthToken,
    isAuthenticated: !!user,
    sessionExpired,
    clearSessionExpired
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
